import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Network, BrainCircuit, Rocket, BarChart4, Terminal, Code2, Cpu, ExternalLink, X, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const iconMap: { [key: string]: React.ElementType } = {
  Zap,
  Network,
  BrainCircuit,
  Rocket,
  BarChart4,
  Terminal,
  Code2,
  Cpu
};

export const Projects = () => {
  const { t } = useTranslation();
  const projects = t('projects.items', { returnObjects: true }) as any[];
  const [selectedProject, setSelectedProject] = useState<any | null>(null);

  return (
    <section className="py-32 bg-slate-950 relative overflow-hidden selection:bg-cyan-500/30">
      {/* Dynamic Background */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950 pointer-events-none" />
      
      {/* Scanning Line Effect */}
      <div className="absolute top-0 left-0 w-full h-1 bg-cyan-500/20 blur-xl animate-scan pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <div>
            
            <h2 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-none">
              {t('projects.hero.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">{t('projects.hero.highlight')}</span>
            </h2>
          </div>
          <div className="max-w-md text-right hidden md:block">
            <p className="text-slate-400 text-sm font-light">
              {t('projects.hero.description')}
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project: any, index: number) => {
            const Icon = iconMap[project.icon] || Code2;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedProject(project)}
                className="group relative bg-slate-900 border border-slate-800 hover:border-cyan-500/50 rounded-2xl p-1 overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-[0_0_40px_rgba(6,182,212,0.15)]"
              >
                {/* Card Content */}
                <div className="relative h-full bg-slate-950/80 rounded-xl p-8 overflow-hidden">
                  {/* Hover Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                  
                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex justify-between items-start mb-8">
                      <div className="w-12 h-12 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-slate-400 group-hover:text-cyan-400 group-hover:border-cyan-500/50 transition-all duration-300">
                        <Icon size={24} />
                      </div>
                      <div className="flex items-center gap-2">
                         <span className="text-[10px] font-mono text-slate-600 group-hover:text-cyan-500 transition-colors">
                           SYS-{String(index + 1).padStart(3, '0')}
                         </span>
                         <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" />
                      </div>
                    </div>

                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors">
                      {project.title}
                    </h3>
                    
                    <p className="text-slate-400 text-sm leading-relaxed mb-8 flex-grow">
                      {project.description}
                    </p>

                    {/* Metrics Mini-Dashboard (Visual Only) */}
                    <div className="mt-auto border-t border-slate-800 pt-6">
                       <div className="grid grid-cols-2 gap-4">
                          <div>
                             <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">{t('projects.ui.uptime')}</div>
                             <div className="text-emerald-400 font-mono text-xs">99.98%</div>
                          </div>
                          <div>
                             <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">{t('projects.ui.efficiency')}</div>
                             <div className="text-cyan-400 font-mono text-xs">+240%</div>
                          </div>
                       </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-3xl overflow-hidden shadow-2xl"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-600" />
              <button 
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors z-10"
              >
                <X size={20} />
              </button>

              <div className="p-8 md:p-10">
                <div className="flex items-center gap-4 mb-6">
                   <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 border border-cyan-500/20">
                     {(() => {
                       const Icon = iconMap[selectedProject.icon] || Code2;
                       return <Icon size={28} />;
                     })()}
                   </div>
                   <div>
                     <div className="text-xs font-mono text-cyan-500 mb-1">{t('projects.ui.accessGranted')}</div>
                     <h3 className="text-3xl font-bold text-white">{selectedProject.title}</h3>
                   </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-slate-950 rounded-xl p-6 border border-slate-800">
                    <p className="text-slate-300 leading-relaxed text-lg">
                      {selectedProject.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                       <h4 className="text-slate-400 text-xs uppercase tracking-wider font-bold mb-2">{t('projects.ui.techStack')}</h4>
                       <div className="flex flex-wrap gap-2">
                          {['React', 'Node.js', 'AI Core'].map((tag, i) => (
                             <span key={i} className="px-2 py-1 rounded-md bg-slate-700 text-xs text-slate-200 font-mono">{tag}</span>
                          ))}
                       </div>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                       <h4 className="text-slate-400 text-xs uppercase tracking-wider font-bold mb-2">{t('projects.ui.performance')}</h4>
                       <div className="flex items-center gap-2 text-emerald-400 font-bold">
                          <Rocket size={16} />
                          <span>{t('projects.ui.velocity')}</span>
                       </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                   {selectedProject.link ? (
                      <a 
                        href={selectedProject.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-600 text-white font-bold hover:bg-cyan-500 transition-colors"
                      >
                        {t('projects.ui.viewLive')} <ExternalLink size={16} />
                      </a>
                   ) : (
                      <button className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-slate-800 text-slate-500 cursor-not-allowed font-bold border border-slate-700">
                        <Lock size={16} /> {t('projects.ui.restricted')}
                      </button>
                   )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`
        @keyframes scan {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .animate-scan {
          animation: scan 8s linear infinite;
        }
      `}</style>
    </section>
  );
};
